import { useCallback, useEffect, useRef, useState } from 'react';
import type { CreatePandaSearchAgent } from '../agent/panda-agent';
import { randomUUID } from '@ag-ui/client';
import { startSearchAnswerRun } from './search-answer-run';
import type { SearchAnswerRun, SearchAnswerSnapshot } from './search-answer-run';
import { SEARCH_ANSWER_STATUS } from './search-answer.constants';
import type { SearchAnswerStatus } from './search-answer.constants';
import type { PandaAnalytics } from '../analytics/panda-analytics';
import { stashHandoff, toHandoffMessages } from '../handoff-bus';

/** Hosts mint a new object per submit, same term included, so a re-search re-runs. */
export interface SearchAnswerSubmission {
  term: string;
  /** Rotates per submit — a same-term resubmit mints a new id, restarting the run. */
  submitId?: string | null;
}

export interface UseSearchAnswerRunOptions {
  submission: SearchAnswerSubmission | null;
  /** Analytics only, never a run key. */
  contextId?: string;
  enabled: boolean;
  createAgent: CreatePandaSearchAgent;
  analytics: PandaAnalytics;
  /** Once per run that ends (done, hidden or error; not aborted) — after an unmount too, if the run outlived the card. */
  onRunEnded?: (snapshot: SearchAnswerSnapshot, submission: SearchAnswerSubmission) => void;
  /**
   * Lets a run outlive the card: `keep` gets every run as it starts, `take` may hand one back to a
   * remounted card, which continues it in place — or shows it as it ended. A resubmit still aborts.
   */
  liveRun?: {
    keep: (live: LiveSearchAnswerRun, submission: SearchAnswerSubmission) => void;
    take: (submission: SearchAnswerSubmission) => LiveSearchAnswerRun | null;
  };
}

export interface LiveSearchAnswerRun {
  agent: ReturnType<CreatePandaSearchAgent>;
  run: SearchAnswerRun;
}

export interface OpenInChatOptions {
  /** Forwarded onto the finished handoff — see `Handoff.acceptOffer`. */
  acceptOffer?: boolean;
}

export interface UseSearchAnswerRun extends SearchAnswerSnapshot {
  isVisible: boolean;
  /** Answer text is on the page (streaming or done) — drives the hosts' no-results copy. */
  hasAnswer: boolean;
  loading: boolean;
  error: boolean;
  /** No local answer, but a hub offer — the card shows the suggestion instead of hiding. */
  offersHubAnswer: boolean;
  openInChat: (options?: OpenInChatOptions) => string;
}

const IDLE: SearchAnswerSnapshot = {
  status: SEARCH_ANSWER_STATUS.IDLE,
  cards: [],
  messages: [],
  traceId: null,
  threadId: '',
  runId: '',
};

export const STREAM_THROTTLE_MS = 80;

/**
 * Rate-limits STREAMING snapshots before they hit React state; any other status
 * applies immediately, flushing a pending streaming frame with it.
 */
export function throttleStreaming(apply: (snapshot: SearchAnswerSnapshot) => void) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastAt = 0;
  let pending: SearchAnswerSnapshot | null = null;

  const clear = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };

  const flush = () => {
    clear();
    if (pending) {
      const snapshot = pending;
      pending = null;
      lastAt = Date.now();
      apply(snapshot);
    }
  };

  return {
    onChange(snapshot: SearchAnswerSnapshot) {
      if (snapshot.status !== SEARCH_ANSWER_STATUS.STREAMING) {
        pending = null;
        clear();
        lastAt = Date.now();
        apply(snapshot);
        return;
      }
      pending = snapshot;
      const wait = STREAM_THROTTLE_MS - (Date.now() - lastAt);
      if (wait <= 0) flush();
      else if (timer === undefined) timer = setTimeout(flush, wait);
    },
    cancel() {
      pending = null;
      clear();
    },
  };
}

/** Answer text is on the page, streaming or done. */
export function hasAnswerStatus(status: SearchAnswerStatus): boolean {
  return status === SEARCH_ANSWER_STATUS.STREAMING || status === SEARCH_ANSWER_STATUS.DONE;
}

/** The run is over and the decision is final: done, hidden, or the suggestion. */
export function isSettledSearchAnswerStatus(status: SearchAnswerStatus): boolean {
  return (
    status === SEARCH_ANSWER_STATUS.DONE ||
    status === SEARCH_ANSWER_STATUS.HIDDEN ||
    status === SEARCH_ANSWER_STATUS.SUGGESTION
  );
}

/** The run is over, one way or another. */
export function isSearchAnswerRunOver(status: SearchAnswerStatus): boolean {
  return isSettledSearchAnswerStatus(status) || status === SEARCH_ANSWER_STATUS.ERROR;
}

/** What the card renders from a snapshot, for hosts that show one the hook did not produce. */
export function toSearchAnswerView({
  status,
}: SearchAnswerSnapshot): Pick<
  UseSearchAnswerRun,
  'isVisible' | 'hasAnswer' | 'loading' | 'error' | 'offersHubAnswer'
> {
  return {
    isVisible: status !== SEARCH_ANSWER_STATUS.IDLE && status !== SEARCH_ANSWER_STATUS.HIDDEN,
    hasAnswer: hasAnswerStatus(status),
    loading: status === SEARCH_ANSWER_STATUS.LOADING,
    error: status === SEARCH_ANSWER_STATUS.ERROR,
    offersHubAnswer: status === SEARCH_ANSWER_STATUS.SUGGESTION,
  };
}

export function useSearchAnswerRun({
  submission,
  contextId,
  enabled,
  createAgent,
  analytics,
  onRunEnded,
  liveRun,
}: UseSearchAnswerRunOptions): UseSearchAnswerRun {
  const [snapshot, setSnapshot] = useState<SearchAnswerSnapshot>(IDLE);
  // openInChat reads it through this ref: as a dependency it would recreate the callback on every streamed frame.
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  // The live run + agent, so "show more" can hand a still-streaming run to the
  // chat. `handoffRef` is keyed on the agent's identity: a re-submit's new run
  // must stay abortable while the handed-off one still streams, and a repeat
  // "show more" on the same run must reopen its conversation, not mint another.
  const agentRef = useRef<ReturnType<CreatePandaSearchAgent> | null>(null);
  const runRef = useRef<SearchAnswerRun | null>(null);
  // The run effect reports with the contextId current at decision time without depending on it.
  const contextIdRef = useRef(contextId);
  contextIdRef.current = contextId;
  const onRunEndedRef = useRef(onRunEnded);
  onRunEndedRef.current = onRunEnded;
  const liveRunRef = useRef(liveRun);
  liveRunRef.current = liveRun;
  // Declared before the run effect so its cleanup runs first on unmount.
  const unmountingRef = useRef(false);
  useEffect(
    () => () => {
      unmountingRef.current = true;
    },
    [],
  );
  const handoffRef = useRef<{
    agent: ReturnType<CreatePandaSearchAgent>;
    threadId: string;
  } | null>(null);

  useEffect(() => {
    const normalizedTerm = submission?.term.trim() ?? '';
    if (!enabled || !submission || !normalizedTerm) {
      setSnapshot(IDLE);
      return undefined;
    }

    const throttled = throttleStreaming(setSnapshot);
    // A host that keeps runs alive may hand one back; it continues (or stands) here. Its own
    // card already reported the decision and reports the end, so neither repeats.
    const adopted = liveRunRef.current?.take(submission) ?? null;
    const agent = adopted?.agent ?? createAgent();
    agentRef.current = agent;
    let run: SearchAnswerRun;
    let unsubscribe = () => {};
    if (adopted) {
      run = adopted.run;
      throttled.onChange(run.latest());
      unsubscribe = run.subscribe(throttled.onChange);
    } else {
      let decisionReported = false;
      run = startSearchAnswerRun({
        agent,
        term: normalizedTerm,
        onChange: snapshot => {
          // Outside the throttle: a run that outlived the card ends after it unmounted.
          if (isSearchAnswerRunOver(snapshot.status)) onRunEndedRef.current?.(snapshot, submission);
          throttled.onChange(snapshot);
          if (decisionReported) return;
          const shown =
            hasAnswerStatus(snapshot.status) || snapshot.status === SEARCH_ANSWER_STATUS.SUGGESTION;
          if (shown || snapshot.status === SEARCH_ANSWER_STATUS.HIDDEN) {
            decisionReported = true;
            analytics.trackSearchAnswerResponse({
              contextId: contextIdRef.current,
              response: shown ? 'shown' : 'hidden',
            });
          }
        },
      });
      liveRunRef.current?.keep({ agent, run }, submission);
    }
    runRef.current = run;

    // Keyed on the submission, NOT contextId — that also regenerates on filter
    // and tab changes. A run handed to the chat streams on there: never abort it.
    // Unmounting keeps the run when the host keeps runs; a resubmit never does.
    return () => {
      unsubscribe();
      throttled.cancel();
      if (handoffRef.current?.agent === agent) return;
      if (unmountingRef.current && liveRunRef.current) return;
      run.abort();
    };
  }, [enabled, submission, createAgent, analytics]);

  // The chat continues under the search run's own thread id: search and chat
  // both set session = thread in Langfuse, so the two land in one session with
  // nothing else to do (PLT-4869). Safe: only chat runs are registered
  // server-side, so a still-streaming search cannot 409 the follow-up, and
  // search persists nothing under its id. Falls back to a fresh id if no
  // agent ever ran.
  const openInChat = useCallback((options?: OpenInChatOptions): string => {
    const agent = agentRef.current;
    const run = runRef.current;
    // The run's own snapshot, not the throttled React state — a click inside the throttle window must not hand over a stale frame.
    const { status, messages, runId } = run?.latest() ?? snapshotRef.current;
    const parentRunId = runId || undefined;

    const previous = handoffRef.current;
    if (agent && previous?.agent === agent) {
      return previous.threadId;
    }

    const threadId = agent?.threadId ?? randomUUID();
    // Still streaming: hand the live run along, or the chat opens on a partial
    // snapshot frozen mid-sentence.
    if (agent && run && status === SEARCH_ANSWER_STATUS.STREAMING) {
      stashHandoff(threadId, {
        kind: 'live',
        live: {
          agent,
          settled: run.settled,
          subscribe: run.subscribeMessages,
          abort: run.abort,
        },
        parentRunId,
      });
    } else {
      // Only a finished handoff may auto-run the offer — a live one is still streaming.
      stashHandoff(threadId, {
        kind: 'complete',
        messages: toHandoffMessages(messages),
        parentRunId,
        acceptOffer: options?.acceptOffer,
      });
    }
    if (agent) handoffRef.current = { agent, threadId };
    return threadId;
  }, []);

  return { ...snapshot, ...toSearchAnswerView(snapshot), openInChat };
}
